import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay,
    faPause,
    faSpinner,
    faBackwardStep,
    faForwardStep,
    faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import GradientThumb from './GradientThumb';
import Helpers from '../Helpers';
import { track as trackEvent } from '../analytics';
import { newSessionId } from '../playback/PlaybackSessionTracker';

const PLAY_LOG_THRESHOLD_MS = 30 * 1000;
const SWIPE_DISMISS_THRESHOLD_PX = 120;

const SharedPlayer = forwardRef(({ tracks, currentTrack, setCurrentTrack, audioFetcher, playLogger, projectId, albumArtUrl, projectName, artistName, onPlayingChange, onLoadingChange }, ref) => {
    const audioRef = useRef(null);
    if (!audioRef.current) audioRef.current = new Audio();

    const [isPlaying, setIsPlaying] = useState(false);
    const [loading, setLoading] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const lastLoggedRef = useRef({ trackId: null, ms: 0, sessionId: null });
    const firedBucketsRef = useRef({}); // { trackId: Set<25|50|75|100> }

    const expandedRef = useRef(null);
    const swipeRef = useRef({ startY: null, dy: 0, scrollAtStart: 0 });

    useEffect(() => {
        if (!expanded) return;
        const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [expanded]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!currentTrack) {
            audio.pause();
            audio.removeAttribute('src');
            audio.load();
            setIsPlaying(false);
            setLoading(false);
            return;
        }

        // Pause the previous src synchronously so its timeupdates stop —
        // otherwise they'd fire between here and audio.src being reassigned
        // and overwrite the elapsed=0 reset below.
        audio.pause();

        let cancelled = false;
        setError(null);
        setLoading(true);
        setElapsed(0);
        setDuration(0);

        (async () => {
            try {
                const { url } = await audioFetcher(currentTrack.id);
                if (cancelled) return;
                audio.src = url;
                audio.currentTime = 0;
                lastLoggedRef.current = { trackId: currentTrack.id, ms: 0, sessionId: newSessionId() };
                firedBucketsRef.current[currentTrack.id] = new Set();
                trackEvent('track_play_started', {
                    track_id: currentTrack.id,
                    project_id: projectId || '',
                    surface: 'share_link',
                    autoplay: false,
                });
                try {
                    await audio.play();
                } catch (_e) {
                    // Autoplay blocked — user can press play.
                    if (!cancelled) setLoading(false);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e.message || "Couldn't load audio");
                    setLoading(false);
                }
            }
        })();

        return () => { cancelled = true; };
    }, [currentTrack, audioFetcher, projectId]);

    useEffect(() => {
        const audio = audioRef.current;
        const onTime = () => {
            setElapsed(audio.currentTime);
            if (
                playLogger &&
                currentTrack &&
                lastLoggedRef.current.trackId === currentTrack.id &&
                lastLoggedRef.current.ms === 0 &&
                audio.currentTime * 1000 >= PLAY_LOG_THRESHOLD_MS
            ) {
                lastLoggedRef.current.ms = audio.currentTime * 1000;
                playLogger(
                    currentTrack.id,
                    Math.floor(audio.currentTime * 1000),
                    lastLoggedRef.current.sessionId,
                ).catch(() => {});
            }
            if (currentTrack && audio.duration > 0) {
                const percent = Math.floor((audio.currentTime / audio.duration) * 100);
                let fired = firedBucketsRef.current[currentTrack.id];
                if (!fired) { fired = new Set(); firedBucketsRef.current[currentTrack.id] = fired; }
                for (const bucket of [25, 50, 75]) {
                    if (percent >= bucket && !fired.has(bucket)) {
                        fired.add(bucket);
                        trackEvent(`track_play_progress_${bucket}`, { track_id: currentTrack.id });
                    }
                }
            }
        };
        const onLoaded = () => setDuration(audio.duration || 0);
        // `play` fires the moment play() is invoked; `playing` fires only when
        // audio is actually outputting samples — that's the correct signal to
        // drop the loading spinner.
        const onPlaying = () => { setIsPlaying(true); setLoading(false); };
        const onWaiting = () => setLoading(true);
        const onPause = () => { setIsPlaying(false); setLoading(false); };
        const onEnded = () => {
            if (currentTrack) {
                const fired = firedBucketsRef.current[currentTrack.id] || new Set();
                if (!fired.has(100)) {
                    fired.add(100);
                    firedBucketsRef.current[currentTrack.id] = fired;
                    trackEvent('track_play_progress_100', { track_id: currentTrack.id });
                }
            }
            if (!currentTrack || !tracks?.length) return;
            const idx = tracks.findIndex((t) => t.id === currentTrack.id);
            const next = tracks[idx + 1];
            if (next) setCurrentTrack(next);
            else { setIsPlaying(false); setLoading(false); }
        };
        const onError = () => {
            if (currentTrack) {
                const t = currentTrack;
                setCurrentTrack(null);
                setTimeout(() => setCurrentTrack(t), 0);
            }
        };

        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('playing', onPlaying);
        audio.addEventListener('waiting', onWaiting);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('error', onError);
        return () => {
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('loadedmetadata', onLoaded);
            audio.removeEventListener('playing', onPlaying);
            audio.removeEventListener('waiting', onWaiting);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('error', onError);
        };
    }, [currentTrack, tracks, setCurrentTrack, playLogger]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (audio.paused) {
            setLoading(true);
            audio.play().catch(() => setLoading(false));
        } else {
            audio.pause();
        }
    };

    useEffect(() => { onPlayingChange?.(isPlaying); }, [isPlaying, onPlayingChange]);
    useEffect(() => { onLoadingChange?.(loading); }, [loading, onLoadingChange]);

    // Media Session: surfaces track / artist / artwork on the iOS lock
    // screen and Android notification, and routes hardware play/pause
    // (lock-screen buttons, headphone clicks) back into the audio element.
    useEffect(() => {
        if (!('mediaSession' in navigator)) return;
        if (!currentTrack) {
            navigator.mediaSession.metadata = null;
            return;
        }
        navigator.mediaSession.metadata = new window.MediaMetadata({
            title: currentTrack.name || '',
            artist: artistName || projectName || '',
            album: projectName || '',
            artwork: albumArtUrl ? [
                { src: albumArtUrl, sizes: '512x512', type: 'image/jpeg' },
            ] : [],
        });
    }, [currentTrack, artistName, projectName, albumArtUrl]);

    useEffect(() => {
        if (!('mediaSession' in navigator)) return;
        const audio = audioRef.current;
        const safe = (handler) => (...args) => { try { handler(...args); } catch (_) {} };
        navigator.mediaSession.setActionHandler('play', safe(() => audio.play()));
        navigator.mediaSession.setActionHandler('pause', safe(() => audio.pause()));
        navigator.mediaSession.setActionHandler('previoustrack', safe(() => skip(-1)));
        navigator.mediaSession.setActionHandler('nexttrack', safe(() => skip(1)));
        return () => {
            navigator.mediaSession.setActionHandler('play', null);
            navigator.mediaSession.setActionHandler('pause', null);
            navigator.mediaSession.setActionHandler('previoustrack', null);
            navigator.mediaSession.setActionHandler('nexttrack', null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tracks, currentTrack]);

    useEffect(() => {
        if (!('mediaSession' in navigator)) return;
        navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    }, [isPlaying]);

    useImperativeHandle(ref, () => ({
        togglePlay,
        playTrack: (track) => setCurrentTrack(track),
    }));

    const skip = (delta) => {
        if (!tracks?.length || !currentTrack) return;
        const idx = tracks.findIndex((t) => t.id === currentTrack.id);
        const next = tracks[idx + delta];
        if (next) setCurrentTrack(next);
    };

    const onSeek = (e) => {
        const audio = audioRef.current;
        const v = Number(e.target.value);
        const t = (audio.duration * v) / 100;
        if (Number.isFinite(t)) {
            audio.currentTime = t;
            setElapsed(t);
        }
    };

    // Swipe-down to dismiss the expanded player. Only engages when the
    // scroll container is at the top — otherwise normal vertical scroll
    // takes precedence.
    const onExpandedTouchStart = (e) => {
        const el = expandedRef.current;
        swipeRef.current = {
            startY: e.touches[0].clientY,
            dy: 0,
            scrollAtStart: el ? el.scrollTop : 0,
        };
    };
    const onExpandedTouchMove = (e) => {
        const el = expandedRef.current;
        if (!el || swipeRef.current.startY == null) return;
        const dy = e.touches[0].clientY - swipeRef.current.startY;
        if (dy > 0 && swipeRef.current.scrollAtStart <= 0) {
            swipeRef.current.dy = dy;
            el.style.transition = 'none';
            el.style.transform = `translateY(${Math.min(dy, 600)}px)`;
        }
    };
    const onExpandedTouchEnd = () => {
        const el = expandedRef.current;
        const dy = swipeRef.current.dy;
        swipeRef.current = { startY: null, dy: 0, scrollAtStart: 0 };
        if (!el) return;
        el.style.transition = 'transform 200ms cubic-bezier(0.2, 0.7, 0.2, 1)';
        if (dy > SWIPE_DISMISS_THRESHOLD_PX) {
            el.style.transform = 'translateY(100%)';
            setTimeout(() => setExpanded(false), 200);
        } else {
            el.style.transform = '';
        }
    };

    if (!currentTrack) return null;

    const totalDuration = duration || currentTrack.duration || 0;
    const progress = totalDuration > 0 ? (elapsed / totalDuration) * 100 : 0;
    const playIcon = loading ? faSpinner : (isPlaying ? faPause : faPlay);
    const playIconClass = loading ? 'track-spin' : '';

    return (
        <>
            <footer className="player-bar shared-player-bar">
                <div key={currentTrack.id} className="shared-player-bar-progress" style={{ width: `${progress}%` }} />
                <button
                    type="button"
                    className="player-bar-left"
                    onClick={() => setExpanded(true)}
                    aria-label="Expand player"
                >
                    {albumArtUrl ? (
                        <img src={albumArtUrl} alt="" className="player-bar-art" />
                    ) : (
                        <GradientThumb id={projectId || 'shared'} className="player-bar-art" />
                    )}
                    <div className="player-bar-info">
                        <div className="player-bar-track">{currentTrack.name}</div>
                        <div className="player-bar-project">
                            {error || artistName || projectName || 'Shared'}
                        </div>
                    </div>
                </button>

                <div className="player-bar-center">
                    <span className="player-bar-time">{Helpers.formatDuration(elapsed)}</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="0.1"
                        value={progress}
                        onChange={onSeek}
                        className="player-bar-seek"
                        style={{ '--progress': `${progress}%` }}
                    />
                    <span className="player-bar-time">{Helpers.formatDuration(totalDuration)}</span>
                </div>

                <div className="player-bar-right">
                    <button className="player-icon" onClick={() => skip(-1)} title="Previous"><FontAwesomeIcon icon={faBackwardStep} /></button>
                    <button className="player-play" onClick={togglePlay} title={isPlaying ? 'Pause' : 'Play'} disabled={loading}>
                        <FontAwesomeIcon icon={playIcon} className={playIconClass} />
                    </button>
                    <button className="player-icon" onClick={() => skip(1)} title="Next"><FontAwesomeIcon icon={faForwardStep} /></button>
                </div>
            </footer>

            {expanded && (
                <div
                    ref={expandedRef}
                    className="player-expanded"
                    role="dialog"
                    aria-label="Now playing"
                    onTouchStart={onExpandedTouchStart}
                    onTouchMove={onExpandedTouchMove}
                    onTouchEnd={onExpandedTouchEnd}
                    onTouchCancel={onExpandedTouchEnd}
                >
                    <div className="player-expanded-grabber" aria-hidden="true" />
                    <div className="player-expanded-header">
                        <button className="player-expanded-close" onClick={() => setExpanded(false)} aria-label="Minimize">
                            <FontAwesomeIcon icon={faChevronDown} />
                        </button>
                        <div className="player-expanded-eyebrow">NOW PLAYING</div>
                        <div className="player-expanded-spacer" />
                    </div>

                    {albumArtUrl ? (
                        <img src={albumArtUrl} alt="" className="player-expanded-art" />
                    ) : (
                        <GradientThumb id={projectId || 'shared'} className="player-expanded-art" />
                    )}

                    <div className="player-expanded-info">
                        <div className="player-expanded-track">{currentTrack.name}</div>
                        <div className="player-expanded-project">
                            {[artistName, projectName].filter(Boolean).join(' · ')}
                        </div>
                    </div>

                    <div className="player-expanded-scrubber">
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="0.1"
                            value={progress}
                            onChange={onSeek}
                            className="player-bar-seek expanded-seek"
                            style={{ '--progress': `${progress}%` }}
                        />
                        <div className="player-expanded-times">
                            <span>{Helpers.formatDuration(elapsed)}</span>
                            <span>{Helpers.formatDuration(totalDuration)}</span>
                        </div>
                    </div>

                    <div className="player-expanded-controls">
                        <button className="player-expanded-icon" onClick={() => skip(-1)} aria-label="Previous">
                            <FontAwesomeIcon icon={faBackwardStep} />
                        </button>
                        <button className="player-expanded-play" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'} disabled={loading}>
                            <FontAwesomeIcon icon={playIcon} className={playIconClass} />
                        </button>
                        <button className="player-expanded-icon" onClick={() => skip(1)} aria-label="Next">
                            <FontAwesomeIcon icon={faForwardStep} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
});

export default SharedPlayer;
